
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { differenceInDays } from 'date-fns';

export const usePeriodHistory = (userId: string | undefined) => {
  const [periodHistory, setPeriodHistory] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [averageCycleLength, setAverageCycleLength] = useState<number | null>(null);
  const [averagePeriodLength, setAveragePeriodLength] = useState<number | null>(null);

  useEffect(() => {
    const fetchPeriodHistory = async () => {
      if (!userId) return;
      
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('period_history')
          .select('*')
          .eq('user_id', userId)
          .order('period_start_date', { ascending: false });
        
        if (error) throw error;
        
        setPeriodHistory(data || []);
        calculateAverages(data || []);
      } catch (error) {
        console.error('Error fetching period history:', error);
        toast.error('Failed to load period history');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPeriodHistory();
  }, [userId]);

  const calculateAverages = (data: any[]) => {
    if (data.length < 2) return;
    
    // Calculate average cycle length
    let totalCycleDays = 0;
    let cycleCount = 0;
    
    for (let i = 0; i < data.length - 1; i++) {
      const currentPeriodStart = new Date(data[i].period_start_date);
      const nextPeriodStart = new Date(data[i + 1].period_start_date);
      const cycleDays = differenceInDays(currentPeriodStart, nextPeriodStart);
      
      if (cycleDays > 0 && cycleDays < 50) { // Filter out unlikely values
        totalCycleDays += cycleDays;
        cycleCount++;
      }
    }
    
    if (cycleCount > 0) {
      setAverageCycleLength(Math.round(totalCycleDays / cycleCount));
    }
    
    // Calculate average period length
    let totalPeriodDays = 0;
    let periodCount = 0;
    
    for (const period of data) {
      if (period.period_length && period.period_length > 0 && period.period_length < 15) {
        totalPeriodDays += period.period_length;
        periodCount++;
      } else if (period.period_end_date) {
        const startDate = new Date(period.period_start_date);
        const endDate = new Date(period.period_end_date);
        const days = differenceInDays(endDate, startDate) + 1;
        
        if (days > 0 && days < 15) {
          totalPeriodDays += days;
          periodCount++;
        }
      }
    }
    
    if (periodCount > 0) {
      setAveragePeriodLength(Math.round(totalPeriodDays / periodCount));
    }
  };

  return {
    periodHistory,
    isLoading,
    averageCycleLength,
    averagePeriodLength
  };
};
